import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Button, Cluster, Tooltip } from "@salesapp/ui";
import { useAccess } from "../../shell/AccessProvider";

export type RecordKind =
  | "company"
  | "lead"
  | "contact"
  | "product"
  | "quote"
  | "order"
  | "invoice"
  | "opportunity"
  | "activity"
  | "support"
  | "calendar"
  | "campaign"
  | "forecast"
  | "sales_cycle"
  | "agreement";

export function RecordObjectIcon({ kind }: { kind: RecordKind }) {
  if (kind === "company") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="8" width="18" height="13" rx="2" fill="currentColor" opacity="0.35" />
        <path d="M7 8V5.5A1.5 1.5 0 0 1 8.5 4h7A1.5 1.5 0 0 1 17 5.5V8" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 12h2M14 12h2M8 16h2M14 16h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "lead") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" fill="currentColor" opacity="0.35" />
        <path d="M5 19c1-3.5 3.5-5.2 7-5.2S18 15.5 19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M16.5 6.5 19 4l1.2 2.2L23 7.5l-2.2 1.2L19 11l-1.2-2.3L15.5 7.5l1-1Z" fill="currentColor" />
      </svg>
    );
  }
  if (kind === "contact") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="3.4" fill="currentColor" opacity="0.35" />
        <path d="M5 19.5c1.1-3.6 3.7-5.4 7-5.4s5.9 1.8 7 5.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
      </svg>
    );
  }
  if (kind === "quote") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="3" width="14" height="18" rx="2" fill="currentColor" opacity="0.35" />
        <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "order") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="6" width="16" height="12" rx="2" fill="currentColor" opacity="0.35" />
        <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "invoice") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="3" width="14" height="18" rx="2" fill="currentColor" opacity="0.35" />
        <path d="M8 8h8M8 12h8M14 16h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="14" rx="2" fill="currentColor" opacity="0.35" />
      <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export type RecordHighlightItem = { label: string; value?: ReactNode; /** Overrides the default tooltip for this label. */ hint?: string };

const HIGHLIGHT_HINTS: Record<string, string> = {
  Status: "Where this record is in its lifecycle.",
  Stage: "The pipeline stage this deal is currently in.",
  Company: "The company this record belongs to.",
  Salesperson: "The sales person this record is assigned to. A sales manager (org admin or CRM app admin) can reassign it.",
  "Created by": "The person who created this record.",
  Contact: "The person at the company this record is about.",
  "Deal value": "Total value of this record in its own currency.",
  Amount: "Estimated value of this lead.",
  Currency: "The currency this document is priced in.",
  Version: "Revision number of this quote. Each edit or regeneration adds a version to the same quote.",
  Opportunity: "The opportunity this quote was raised from.",
  Lead: "The lead this record originated from.",
  "Lead ID": "Unique code of the lead this record came from.",
  "Opportunity ID": "Unique code of this opportunity.",
  "Latest quote": "The most recent quote raised on this opportunity.",
  Order: "The order created from this record.",
  Quote: "The quote this record was created from.",
  Invoice: "The invoice created from this record.",
  Temperature: "How warm the lead is (hot, warm or cold), set by the owner.",
  Source: "Where this lead came from, such as referral or web form.",
  Score: "Lead score. Higher means a better fit and more engagement.",
  Email: "Primary email address for this record.",
  Phone: "Primary phone number for this record.",
  Industry: "The industry the company operates in.",
  Territory: "The sales territory this record is assigned to.",
  "Company code": "Unique code of this company.",
  "Billing address": "Address invoices for this company are sent to.",
  Opened: "Date this ticket was opened.",
  Dates: "Start and end dates of this agreement.",
};

function highlightHint(kind: RecordKind | undefined, item: RecordHighlightItem): string | undefined {
  if (item.hint) return item.hint;
  if (item.label === "Aging") {
    if (kind === "opportunity") return "How long this deal has been in its current stage.";
    if (kind === "support") return "How long this ticket has been open.";
    return "How long this record has been open.";
  }
  if (/ number$/.test(item.label)) return `Unique reference number of this ${item.label.replace(/ number$/, "").toLowerCase()}.`;
  return HIGHLIGHT_HINTS[item.label];
}

export function RecordHighlight({ items, kind }: { items: RecordHighlightItem[]; kind?: RecordKind }) {
  const visible = items.filter((item) => item.value != null && item.value !== "");
  if (visible.length === 0) return null;
  return (
    <dl className="crm-record-highlights">
      {visible.map((item) => {
        const hint = highlightHint(kind, item);
        return (
          <div key={item.label} className="crm-record-highlight">
            <dt>
              {hint ? (
                <Tooltip text={hint} label={`About ${item.label}`}>
                  <span>{item.label}</span>
                </Tooltip>
              ) : (
                item.label
              )}
            </dt>
            <dd>{item.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function RecordWorkspace({
  kind,
  objectLabel,
  title,
  subtitle,
  backTo,
  backLabel,
  highlights,
  actions,
  tabs,
  activeTab,
  onTabChange,
  children,
  aside,
  preview = false,
}: {
  kind: RecordKind;
  objectLabel: string;
  title: string;
  subtitle?: ReactNode;
  backTo: string;
  backLabel: string;
  highlights: RecordHighlightItem[];
  actions?: ReactNode;
  tabs: Array<{ key: string; label: string }>;
  activeTab: string;
  onTabChange: (key: string) => void;
  children: ReactNode;
  aside?: ReactNode;
  preview?: boolean;
}) {
  return (
    <div className={preview ? "crm-record crm-record--preview" : "crm-record"}>
      {preview ? <p className="crm-back crm-back--preview">{backLabel}</p> : <Link to={backTo} className="crm-back">{backLabel}</Link>}
      <header className="crm-record-header">
        <div className="crm-record-header-main">
          <Cluster gap={3} align="start">
            <span className="crm-record-icon" aria-hidden>
              <RecordObjectIcon kind={kind} />
            </span>
            <div className="crm-record-heading">
              <div className="crm-record-object">{objectLabel}</div>
              <h1 className="crm-record-title">{title}</h1>
              {subtitle ? <div className="crm-record-subtitle">{subtitle}</div> : null}
            </div>
          </Cluster>
          <RecordHighlight items={highlights} kind={kind} />
        </div>
        {actions ? <div className="crm-record-actions">{actions}</div> : null}
      </header>

      <div className="crm-record-tabs" role="tablist" aria-label={`${objectLabel} sections`}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`crm-record-tab${activeTab === tab.key ? " is-active" : ""}`}
            data-tour={`record-tab-${tab.key}`}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              onTabChange(tab.key);
            }}
            onClick={() => onTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={aside ? "crm-record-body" : "crm-record-body crm-record-body--solo"}>
        <div className="crm-record-main">{children}</div>
        {aside ? <aside className="crm-record-aside">{aside}</aside> : null}
      </div>
    </div>
  );
}

export function DetailSection({
  title,
  children,
  className,
  tools,
  onTitleClick,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  tools?: ReactNode;
  onTitleClick?: () => void;
}) {
  return (
    <section className={["crm-detail-section", className].filter(Boolean).join(" ")}>
      {onTitleClick ? (
        <button
          type="button"
          className="crm-detail-section-title is-editable"
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            onTitleClick();
          }}
          onClick={onTitleClick}
        >
          {title}
        </button>
      ) : (
        <h2 className="crm-detail-section-title">{title}</h2>
      )}
      {tools}
      <div className="crm-field-grid">{children}</div>
    </section>
  );
}

export function FieldItem({
  label,
  value,
  wide = false,
  selected = false,
  onSelect,
  meta,
  hint,
}: {
  label: string;
  value?: ReactNode;
  wide?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  meta?: string;
  /** Info-mark explanation of what this field means. */
  hint?: string;
}) {
  const className = [
    "crm-field-item",
    wide ? "is-wide" : "",
    selected ? "is-selected" : "",
    onSelect ? "is-interactive" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const body = (
    <>
      <div className="crm-field-item-label">
        {hint ? (
          <Tooltip text={hint} label={`About ${label}`}>
            <span>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
        {meta ? <small>{meta}</small> : null}
      </div>
      <div className="crm-field-item-value">{value ?? "—"}</div>
    </>
  );
  if (onSelect) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={className}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          onSelect();
        }}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onSelect();
        }}
      >
        {body}
      </div>
    );
  }
  return <div className={className}>{body}</div>;
}

export function ListWorkspace({
  title,
  actions,
  toolbar,
  children,
}: {
  title: string;
  actions?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="crm-list-workspace">
      <div className="crm-page-header">
        <h1 className="crm-page-title">{title}</h1>
        {actions ? <div className="crm-record-actions">{actions}</div> : null}
      </div>
      {toolbar}
      {children}
    </div>
  );
}

const DELETE_CONFIRM =
  "This record will sit in the recycle bin for 30 days, then be removed automatically. Restore it from Organization settings → Recycle bin.";

export function DeleteRecordButton({
  capability,
  recordLabel,
  onDeleted,
  size = "md",
  variant = "danger",
}: {
  capability: string;
  recordLabel: string;
  onDeleted: () => Promise<void> | void;
  size?: "sm" | "md";
  variant?: "danger" | "ghost";
}) {
  const { can } = useAccess();
  const [busy, setBusy] = useState(false);
  if (!can(capability, "delete")) return null;

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={busy}
      title={DELETE_CONFIRM}
      onClick={() => {
        if (!confirm(`Delete ${recordLabel}?\n\n${DELETE_CONFIRM}`)) return;
        setBusy(true);
        void Promise.resolve(onDeleted()).finally(() => setBusy(false));
      }}
    >
      {busy ? "Deleting…" : "Delete"}
    </Button>
  );
}
